import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import scheduleRecommendationsApiService from '@/services/scheduleRecommendationsApi';
import type {
  WeeklyScheduleRecommendation,
  WeeklyScheduleSummary,
  ScheduleZoneDay,
  ZoneNeed,
  RegenerateRecommendationRequest,
  RegenerateRecommendationResponse,
  ListRecommendationsParams,
  UpdateScheduleDayParams,
  ScheduleDayUpdateResponse,
} from '@/types/scheduleRecommendations';

interface ScheduleRecommendationsState {
  // Recommendations list
  recommendations: WeeklyScheduleSummary[];
  selectedRecommendation: WeeklyScheduleRecommendation | null;
  scheduleDays: ScheduleZoneDay[];

  // Zone needs
  zoneNeeds: ZoneNeed[];

  // Regeneration state
  regenerationInProgress: boolean;
  regenerationResult: RegenerateRecommendationResponse | null;

  // UI state
  loading: boolean;
  error: string | null;

  // Last updated timestamp
  lastUpdated: number | null;
}

const initialState: ScheduleRecommendationsState = {
  recommendations: [],
  selectedRecommendation: null,
  scheduleDays: [],
  zoneNeeds: [],
  regenerationInProgress: false,
  regenerationResult: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async Thunks

/**
 * Generate a new weekly schedule recommendation
 */
export const generateWeeklyRecommendation = createAsyncThunk(
  'scheduleRecommendations/generate',
  async (
    params: {
      farmId: string;
      week_start_date?: string;
      zone_id?: number;
      apply_seasonal_allocation?: boolean;
      notes?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await scheduleRecommendationsApiService.generateWeeklyRecommendation(
        params.farmId,
        {
          week_start_date: params.week_start_date,
          zone_id: params.zone_id,
          apply_seasonal_allocation: params.apply_seasonal_allocation,
          notes: params.notes,
        }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to generate recommendation'
      );
    }
  }
);

/**
 * List schedule recommendations for a farm
 */
export const listRecommendations = createAsyncThunk(
  'scheduleRecommendations/list',
  async (
    params: {
      farmId: string;
      filters?: ListRecommendationsParams;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await scheduleRecommendationsApiService.listRecommendations(
        params.farmId,
        params.filters
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to list recommendations'
      );
    }
  }
);

/**
 * Get a specific recommendation by ID
 */
export const getRecommendation = createAsyncThunk(
  'scheduleRecommendations/get',
  async (recommendationId: number, { rejectWithValue }) => {
    try {
      const response = await scheduleRecommendationsApiService.getRecommendation(recommendationId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to get recommendation'
      );
    }
  }
);

/**
 * Get schedule days for a recommendation
 */
export const getScheduleDays = createAsyncThunk(
  'scheduleRecommendations/getDays',
  async (recommendationId: number, { rejectWithValue }) => {
    try {
      const response = await scheduleRecommendationsApiService.getScheduleDays(recommendationId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to get schedule days'
      );
    }
  }
);

/**
 * Update a schedule day
 */
export const updateScheduleDay = createAsyncThunk(
  'scheduleRecommendations/updateDay',
  async (
    params: {
      recommendationId: number;
      dayId: number;
      updates: UpdateScheduleDayParams;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await scheduleRecommendationsApiService.updateScheduleDay(
        params.recommendationId,
        params.dayId,
        params.updates
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update schedule day'
      );
    }
  }
);

/**
 * Delete a schedule recommendation
 */
export const deleteRecommendation = createAsyncThunk(
  'scheduleRecommendations/delete',
  async (recommendationId: number, { rejectWithValue }) => {
    try {
      await scheduleRecommendationsApiService.deleteRecommendation(recommendationId);
      return recommendationId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to delete recommendation'
      );
    }
  }
);

/**
 * Get zone irrigation needs
 */
export const getZoneNeeds = createAsyncThunk(
  'scheduleRecommendations/getZoneNeeds',
  async (
    params: {
      farmId: string;
      week_start?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await scheduleRecommendationsApiService.getZoneNeeds(
        params.farmId,
        params.week_start
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to get zone needs'
      );
    }
  }
);

/**
 * Regenerate a weekly recommendation with updated forecast and water balance
 */
export const regenerateRecommendation = createAsyncThunk(
  'scheduleRecommendations/regenerate',
  async (
    params: {
      recommendationId: number;
      options?: RegenerateRecommendationRequest;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await scheduleRecommendationsApiService.regenerateWeeklyRecommendation(
        params.recommendationId,
        {
          cascade_following_weeks: params.options?.cascade_following_weeks,
          notes: params.options?.notes,
        }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to regenerate recommendation'
      );
    }
  }
);

/**
 * Update recommendation status
 */
export const updateRecommendationStatus = createAsyncThunk(
  'scheduleRecommendations/updateStatus',
  async (
    params: {
      recommendationId: number;
      status: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await scheduleRecommendationsApiService.updateRecommendationStatus(
        params.recommendationId,
        params.status
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update recommendation status'
      );
    }
  }
);

// Slice
const scheduleRecommendationsSlice = createSlice({
  name: 'scheduleRecommendations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRegenerationResult: (state) => {
      state.regenerationResult = null;
    },
    setSelectedRecommendation: (state, action: PayloadAction<WeeklyScheduleRecommendation | null>) => {
      state.selectedRecommendation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Generate recommendation
      .addCase(generateWeeklyRecommendation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateWeeklyRecommendation.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedRecommendation = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(generateWeeklyRecommendation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // List recommendations
      .addCase(listRecommendations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listRecommendations.fulfilled, (state, action) => {
        state.loading = false;
        state.recommendations = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(listRecommendations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get recommendation
      .addCase(getRecommendation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRecommendation.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedRecommendation = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(getRecommendation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get schedule days
      .addCase(getScheduleDays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getScheduleDays.fulfilled, (state, action) => {
        state.loading = false;
        state.scheduleDays = action.payload;
      })
      .addCase(getScheduleDays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update schedule day
      .addCase(updateScheduleDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateScheduleDay.fulfilled, (state, action) => {
        state.loading = false;
        const updatedDay = action.payload.schedule_day;
        const index = state.scheduleDays.findIndex(day => day.id === updatedDay.id);
        if (index !== -1) {
          state.scheduleDays[index] = updatedDay;
        }
      })
      .addCase(updateScheduleDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete recommendation
      .addCase(deleteRecommendation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecommendation.fulfilled, (state, action) => {
        state.loading = false;
        state.recommendations = state.recommendations.filter(
          rec => rec.id !== action.payload
        );
        if (state.selectedRecommendation?.id === action.payload) {
          state.selectedRecommendation = null;
        }
      })
      .addCase(deleteRecommendation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get zone needs
      .addCase(getZoneNeeds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getZoneNeeds.fulfilled, (state, action) => {
        state.loading = false;
        state.zoneNeeds = action.payload;
      })
      .addCase(getZoneNeeds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Regenerate recommendation
      .addCase(regenerateRecommendation.pending, (state) => {
        state.regenerationInProgress = true;
        state.regenerationResult = null;
        state.error = null;
      })
      .addCase(regenerateRecommendation.fulfilled, (state, action) => {
        state.regenerationInProgress = false;
        state.regenerationResult = action.payload;

        // Update the recommendation in the list
        const recIndex = state.recommendations.findIndex(
          rec => rec.id === action.payload.recommendation_id
        );
        if (recIndex !== -1) {
          state.recommendations[recIndex].status = action.payload.status as any;
        }

        // Update selected recommendation if it's the one that was regenerated
        if (state.selectedRecommendation?.id === action.payload.recommendation_id) {
          state.selectedRecommendation.status = action.payload.status as any;
        }

        state.lastUpdated = Date.now();
      })
      .addCase(regenerateRecommendation.rejected, (state, action) => {
        state.regenerationInProgress = false;
        state.error = action.payload as string;
      })

      // Update recommendation status
      .addCase(updateRecommendationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecommendationStatus.fulfilled, (state, action) => {
        state.loading = false;

        // Update in list
        const recIndex = state.recommendations.findIndex(
          rec => rec.id === action.payload.id
        );
        if (recIndex !== -1) {
          state.recommendations[recIndex].status = action.payload.status;
        }

        // Update selected recommendation
        if (state.selectedRecommendation?.id === action.payload.id) {
          state.selectedRecommendation = action.payload;
        }

        state.lastUpdated = Date.now();
      })
      .addCase(updateRecommendationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  clearError,
  clearRegenerationResult,
  setSelectedRecommendation,
} = scheduleRecommendationsSlice.actions;

// Selectors
export const selectRecommendations = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.recommendations;

export const selectSelectedRecommendation = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.selectedRecommendation;

export const selectScheduleDays = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.scheduleDays;

export const selectZoneNeeds = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.zoneNeeds;

export const selectRecommendationsLoading = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.loading;

export const selectRecommendationsError = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.error;

export const selectRegenerationInProgress = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.regenerationInProgress;

export const selectRegenerationResult = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.regenerationResult;

export const selectLastUpdated = (state: { scheduleRecommendations: ScheduleRecommendationsState }) =>
  state.scheduleRecommendations.lastUpdated;

export default scheduleRecommendationsSlice.reducer;
